import React, { useEffect, useState } from 'react';
type Size = 'sm' | 'md' | 'lg' | 'xl';
const SIZE: Record<Size, string> = {
  sm: 'h-9 w-9 text-body-sm',
  md: 'h-11 w-11 text-body',
  lg: 'h-16 w-16 text-h4',
  xl: 'h-28 w-28 text-h2'
};
interface AvatarProps {
  /** The person's real photo. Absent means they have not uploaded one. */
  src?: string | null;
  initials: string;
  name: string;
  size?: Size;
  /** A small dot indicating presence or verification, positioned on the ring. */
  badge?: React.ReactNode;
  className?: string;
}

/**
 * One avatar, everywhere.
 *
 * A photo is shown only when the person actually has one; otherwise their
 * initials are the fallback, which is a real state rather than a broken image.
 * A photo that fails to load falls back to the same initials instead of leaving
 * an empty circle, and the loading state is the initials too — so the avatar
 * never flashes blank.
 *
 * The image is decorative here: the person's name is always rendered beside it,
 * so an alt text repeating it would be read twice by a screen reader.
 */
export function Avatar({
  src,
  initials,
  name,
  size = 'md',
  badge,
  className = ''
}: AvatarProps) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  const showPhoto = Boolean(src) && !failed;
  return <span className={`relative inline-flex shrink-0 ${className}`} data-id="element-5827" data-mp="3vpm8daeimjqa">
      <span className={`flex items-center justify-center overflow-hidden rounded-full border border-line bg-brand-50 font-semibold text-brand-800 ${SIZE[size]}`} data-id="element-5828" data-mp="4ijh6iyogdg2">
        {showPhoto ? <img src={src as string} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} data-id="element-5829" data-mp="dig4ag5f5490" /> : <span aria-hidden data-id="element-5830" data-mp="2fakpl8sex3ar">{initials}</span>}
        <span className="sr-only" data-id="element-5831" data-mp="2fbvuxd1iaj3z">{name}</span>
      </span>
      {badge && <span className="absolute -bottom-0.5 -right-0.5" data-id="element-5832" data-mp="2k8mbe1h83gpx">{badge}</span>}
    </span>;
}